import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Center, Divider, Stack, Text } from '@mantine/core';
import { UseMutationResult } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { AddButtonLink, Content, TextInputControl } from '@/core/components';

import { useAppNotification } from '@feature/utils';

import { ProjectFoundModal } from './ProjectFoundModal';
import { useReclaimAuthorship } from './model/ProjectQuery';
import { useSearchProjectByIdentifiers } from './model/ProjectQuery';
import { ProjectReclaimSearch, ReclaimRequest } from './model/ProjectTypes';
import { ProjectSearchRequest } from './model/ProjectTypes';

const schema = z.object({
  title: z.string().optional(),
  contractNo: z.string().optional(),
  registerNo: z.string().optional(),
});

const defaultValues = {
  title: '',
  contractNo: '',
  registerNo: '',
};

interface ProjectSearchClaimableProps {
  useSearchProjectHook?: () => UseMutationResult<ProjectReclaimSearch, Error, ProjectSearchRequest, unknown>;
}

export function ProjectSearchClaimable(props: ProjectSearchClaimableProps) {
  const { useSearchProjectHook = useSearchProjectByIdentifiers } = props;

  const form = useForm<ProjectSearchRequest>({
    defaultValues,
    resolver: zodResolver(schema),
  });
  const { t } = useTranslation();

  const { mutate, data, isPending, reset } = useSearchProjectHook();

  const notification = useAppNotification();
  const onSubmit = (values: ProjectSearchRequest) => {
    // Only submit if at least one field has a value
    const hasValue = values.title?.trim() || values.contractNo?.trim() || values.registerNo?.trim();
    if (hasValue) {
      mutate(values, {
        onError: notification.error,
        onSuccess: notification.success,
      });
    }
  };

  const reclaimMutation = useReclaimAuthorship();
  const isSubmitting = isPending || reclaimMutation.isPending;

  const navigate = useNavigate();
  const onReclaimAuthorship = (request: ReclaimRequest) => {
    reset();
    reclaimMutation.mutate(request, {
      onError: notification.error,
      onSuccess: () => {
        notification.success();
        navigate({ to: `../${request.projectId}` });
      },
    });
  };

  return (
    <Content type="full">
      <Text p="md">{t('projects.searchInfo')}</Text>
      {data && 'project' in data && (
        <ProjectFoundModal
          close={reset}
          data={data}
          onReclaimAuthorship={onReclaimAuthorship}
        />
      )}
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Stack gap="xs">
            <TextInputControl<ProjectSearchRequest>
              name="title"
              leftSectionWidth="60px"
              leftSection={<Text fw="500">Title:</Text>}
              label={t('projects.form.fields.title')}
              isSubmitting={isSubmitting}
            />
            <Text
              ta="center"
              fw="500"
            >
              sau
            </Text>
            <TextInputControl<ProjectSearchRequest>
              name="contractNo"
              leftSectionWidth="100px"
              leftSection={<Text fw="500">Contract No:</Text>}
              label={t('projects.form.fields.contractNo')}
              isSubmitting={isSubmitting}
            />
            <Text
              ta="center"
              fw="500"
            >
              sau
            </Text>
            <TextInputControl<ProjectSearchRequest>
              name="registerNo"
              leftSectionWidth="100px"
              leftSection={<Text fw="500">Register No:</Text>}
              label={t('projects.form.fields.registerNo')}
              isSubmitting={isSubmitting}
            />
            <Center>
              <Button
                loading={isSubmitting}
                type="submit"
              >
                {t('button.search')}
              </Button>
            </Center>
          </Stack>
        </form>
      </FormProvider>
      <Divider />
      <Text
        c="dimmed"
        fs="italic"
        mt="10px"
      >
        {t('projects.addNewDisclaimer')}
      </Text>
      {!isSubmitting && (
        <AddButtonLink
          to="../create"
          name={t('button.addNew')}
        />
      )}
    </Content>
  );
}
